import * as React from "react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {Editor, Transforms} from "slate";

import {
    CoeditorAPIContext,
    CoeditorContext,
    setCoeditorInitialCompletionFinished,
    setCoeditorStarted,
} from "@khan/guide-coeditor-core";
import Log from "@khan/logging";
import {useLocation} from "@khan/urls";

import {chunkify} from "./chunkify.ts";
import useCoeditingCommandListener from "./hooks/use-coediting-command-handler.ts";
import useGetLatestSnapshot from "./hooks/use-get-latest-snapshot.ts";
import createSlateEditor from "./slate/create-coeditor.ts";
import {
    clearAllSkeletons,
    clearSkeletonWithPlaceholderKey,
} from "./slate/helpers/skeleton-helpers.ts";
import type {RestorableCoeditingSnapshot} from "./types.ts";

const CoeditorContextProvider = (props: {
    initialEditor?: Editor;
    children: React.ReactNode;
}): React.ReactElement => {
    const {initialEditor, children} = props;

    const [open, setOpen] = useState(false);
    const [readOnly, setReadOnly] = useState(false);
    const [documentTitle, setDocumentTitle] = useState("");
    const [currentPlaceholderKey, setCurrentPlaceholderKey] = useState("");
    const [isKhanmigoTyping, setKhanmigoTyping] = useState(false);
    const [editor, setEditor] = useState(
        () => initialEditor ?? createSlateEditor(),
    );
    const {queryParams} = useLocation();
    const getCoeditingSnapshot = useGetLatestSnapshot();

    const restoreSnapshot = useCallback(
        (snapshot: RestorableCoeditingSnapshot) => {
            setOpen(true);
            setCoeditorStarted(true);
            setDocumentTitle(snapshot.title);
            editor.insertTemplateChunks(
                chunkify(
                    snapshot.prefix + snapshot.selection + snapshot.suffix,
                ),
            );
            setCoeditorInitialCompletionFinished();
        },
        [editor],
    );

    useEffect(() => {
        getCoeditingSnapshot(queryParams.resumeThreadId).then((snapshot) => {
            if (snapshot) {
                restoreSnapshot(snapshot);
            }
        });
    }, [queryParams.resumeThreadId, getCoeditingSnapshot, restoreSnapshot]);

    useCoeditingCommandListener(
        (placeholderKey, chunk) => {
            if (placeholderKey) {
                editor.insertChunkAtPlaceholder(placeholderKey, chunk);
            } else {
                editor.insertChunkAtSelection(chunk);
            }
        },
        (event) => {
            switch (event.event) {
                case "template-structure": {
                    editor.insertTemplateChunks(event.chunks);
                    return;
                }

                case "edit-started": {
                    setOpen(true);
                    setCoeditorStarted(true);
                    setReadOnly(true);
                    setKhanmigoTyping(true);
                    editor.startKhanmigoEditing();
                    return;
                }

                case "edit-finished": {
                    setReadOnly(false);
                    setKhanmigoTyping(false);
                    editor.stopKhanmigoEditing();
                    editor.rerenderTextNodes();
                    return;
                }

                case "placeholder-completion-started": {
                    setCurrentPlaceholderKey(event.placeholderKey);
                    return;
                }

                case "placeholder-completion-finished": {
                    clearSkeletonWithPlaceholderKey(
                        editor,
                        event.placeholderKey,
                    );
                    setCurrentPlaceholderKey("");
                    return;
                }

                case "initial-completion-finished": {
                    clearAllSkeletons(editor);
                    setCurrentPlaceholderKey("");
                    setCoeditorInitialCompletionFinished();

                    if (!documentTitle) {
                        setDocumentTitle(editor.getDocumentTitle());
                    }
                    return;
                }

                case "undo": {
                    editor.undo();
                    return;
                }

                case "set-read-only": {
                    setReadOnly(event.value);
                    return;
                }

                case "set-title": {
                    setDocumentTitle(event.newTitle);
                    return;
                }

                case "selection-changed": {
                    // If we got a move instruction, we clear the editor and insert the
                    // new prefix, then selection, then suffix, keeping track of the points
                    // before and after the selection: this will be the new selected range.
                    editor.clear();
                    Editor.insertText(editor, event.newPrefix);
                    const anchor = Editor.end(editor, []);
                    Editor.insertText(editor, event.newSelection);
                    const focus = Editor.end(editor, []);
                    Editor.insertText(editor, event.newSuffix);
                    Transforms.select(editor, {anchor, focus});
                    return;
                }

                default: {
                    Log.error("received unknown coediting event:", event);
                }
            }
        },
    );

    const setters = useMemo(
        () => ({
            setReadOnly,
            setDocumentTitle,
            restoreSnapshot,
            reset: () => {
                setKhanmigoTyping(false);
                setReadOnly(false);
                setOpen(false);
                setDocumentTitle("");
                setEditor(createSlateEditor());
            },
        }),
        [restoreSnapshot],
    );

    const getters = useMemo(
        () => ({
            editor,
            open,
            currentPlaceholderKey,
            readOnly,
            documentTitle,
            isKhanmigoTyping,
        }),
        [
            editor,
            open,
            currentPlaceholderKey,
            readOnly,
            documentTitle,
            isKhanmigoTyping,
        ],
    );

    return (
        <CoeditorAPIContext.Provider value={setters}>
            <CoeditorContext.Provider value={getters}>
                {children}
            </CoeditorContext.Provider>
        </CoeditorAPIContext.Provider>
    );
};

export default CoeditorContextProvider;
